import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import StringUtils from 'services/StringUtils';

const ContentEditable = ({ name, text, placeholder, onChange, onKeyDown, disabled }) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const editableRef = useRef(null);
  const lastTextRef = useRef(text);
  const initializedRef = useRef(false);

  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    if (!initializedRef.current) {
      el.innerText = text || '';
      initializedRef.current = true;
      lastTextRef.current = text;
      setIsEmpty(StringUtils.isEmpty(text?.trim()));
      return;
    }
    if (text !== el.innerText) {
      el.innerText = text;
      lastTextRef.current = text;
      setIsEmpty(StringUtils.isEmpty(text?.trim()));
    }
  }, [text]);

  const emitChange = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    const currentText = el.innerText;
    if (onChange && currentText !== lastTextRef.current) {
      onChange({
        target: {
          value: currentText,
        },
      });
    }
    lastTextRef.current = currentText;
    const trimmedEmpty = currentText.trim().length === 0;
    setIsEmpty(trimmedEmpty);

    // This is needed to fix a rendering bug in safari
    forceRedraw(el);
  }, [onChange]);

  return (
    <div className="content-editable-wrapper" style={{ width: '100%', position: 'relative' }}>
      <div
        ref={editableRef}
        id={name}
        dir="ltr"
        className={'content-editable' + (isEmpty ? ' is-empty' : '')}
        suppressContentEditableWarning={true}
        onInput={emitChange}
        onBlur={emitChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        contentEditable
      />
    </div>
  );
};

function forceRedraw(el) {
  const originalDisplay = el.style.display;
  el.style.display = 'none';
  el.offsetHeight;
  el.style.display = originalDisplay;
}

ContentEditable.propTypes = {
  text: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ContentEditable;
